import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { Loader2, Mail, CheckCircle2 } from "lucide-react";

const AccountSettings = () => {
  const { user, resetPasswordForEmail } = useAuth();
  const initialFirstName = user?.user_metadata?.first_name || user?.user_metadata?.full_name?.split(" ")[0] || "";
  const initialLastName = user?.user_metadata?.last_name || user?.user_metadata?.full_name?.split(" ").slice(1).join(" ") || "";
  const [firstName, setFirstName] = useState(initialFirstName);
  const [lastName, setLastName] = useState(initialLastName);
  const [savedProfile, setSavedProfile] = useState({
    firstName: initialFirstName,
    lastName: initialLastName,
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [sendingResetLink, setSendingResetLink] = useState(false);
  const [resetLinkSent, setResetLinkSent] = useState(false);

  // Check auth providers. User.identities contains an entry per linked provider
  const providers = user?.identities?.map((i) => i.provider) || [];
  const hasEmailProvider = providers.includes("email");
  const profileChanged = firstName !== savedProfile.firstName || lastName !== savedProfile.lastName;

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !profileChanged) return;
    setSavingProfile(true);
    try {
      const { error } = await supabase.auth.updateUser({
        data: { first_name: firstName, last_name: lastName, full_name: `${firstName} ${lastName}`.trim() },
      });
      if (error) throw error;
      setSavedProfile({ firstName, lastName });
      toast({ title: "Profile updated" });
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSendResetLink = async () => {
    if (!user?.email) {
      toast({ title: "No email found", description: "Unable to send reset link.", variant: "destructive" });
      return;
    }
    setSendingResetLink(true);
    try {
      const { error } = await resetPasswordForEmail(user.email);
      if (error) throw error;
      setResetLinkSent(true);
      toast({ title: "Password reset link sent", description: "Check your email inbox for the reset link." });
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setSendingResetLink(false);
    }
  };

  return (
    <main>
      <form className="space-y-8" onSubmit={handleUpdateProfile}>
        <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 space-y-1">
            <h1 className="text-xl font-heading font-semibold text-foreground">Account settings</h1>
            <p className="text-sm text-muted-foreground">
              Manage your account details and password.
            </p>
          </div>
          <Button
            type="submit"
            size="sm"
            className="standard-action-button shrink-0 self-start"
            disabled={!profileChanged || savingProfile}
          >
            {savingProfile && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save changes
          </Button>
        </header>

        <section aria-labelledby="account-details-heading">
          <div className="mb-4 flex items-center gap-4">
            <h2 id="account-details-heading" className="shrink-0 font-heading text-base font-semibold text-foreground">
              Account details
            </h2>
            <div className="h-px flex-1 bg-border" aria-hidden="true" />
          </div>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="account-email">Email</Label>
              <Input id="account-email" value={user?.email || ""} disabled />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="account-first-name">First name</Label>
                <Input
                  id="account-first-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First name"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="account-last-name">Last name</Label>
                <Input
                  id="account-last-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Last name"
                />
              </div>
            </div>
          </div>
        </section>

        <section aria-labelledby="password-heading">
          <div className="mb-4 flex items-center gap-4">
            <h2 id="password-heading" className="shrink-0 font-heading text-base font-semibold text-foreground">
              Password
            </h2>
            <div className="h-px flex-1 bg-border" aria-hidden="true" />
          </div>
          {!hasEmailProvider ? (
            /* Google-only sign-up. No Dibs password exists */
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-full bg-blue-50 dark:bg-blue-950 flex items-center justify-center shrink-0 mt-0.5">
                <svg className="w-4.5 h-4.5" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" fill="#4285F4" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                </svg>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium">Signed in with Google</p>
                <p className="text-sm text-muted-foreground">
                  Your account is linked to Google, so your password is managed by Google.
                  To change your password, visit your{" "}
                  <a
                    href="https://myaccount.google.com/security"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    Google Account settings
                  </a>.
                </p>
              </div>
            </div>
          ) : resetLinkSent ? (
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 shrink-0" />
              <div className="space-y-1">
                <p className="text-sm font-medium text-green-800 dark:text-green-300">Reset link sent!</p>
                <p className="text-sm text-green-700 dark:text-green-400/80">
                  We've sent a password reset link to <span className="font-medium">{user?.email}</span>.
                  Check your inbox and click the link to set your new password.
                </p>
                <Button
                  type="button"
                  variant="link"
                  className="h-auto p-0 text-sm text-green-700 dark:text-green-400 underline"
                  onClick={() => { setResetLinkSent(false); }}
                >
                  Send another link
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                For security, password changes require email verification. Click the button below and we'll send a reset link to <span className="font-medium text-foreground">{user?.email}</span>.
              </p>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="standard-action-button"
                onClick={handleSendResetLink}
                disabled={sendingResetLink}
              >
                {sendingResetLink ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Mail className="w-4 h-4 mr-2" />
                )}
                Send password reset link
              </Button>
            </div>
          )}
        </section>
      </form>
    </main>
  );
};

export default AccountSettings;
